import React from "react";
import { createTheme } from "@mui/material/styles";
import { commonStyles } from "./components/common/commonStyles";
import { switchSx } from "./components/common/dashboard/switchStyles";
import ChoiceControlIcon from "./components/common/dashboard/ChoiceControlIcon";

// Global CMP MUI theme (shared dashboard consistency pass, SH-001/SH-002).
//
// Before this theme existed there was no ThemeProvider at all, so every MUI
// control without a local `sx` rendered the MUI default blue (#1976d2) for
// checked/focused states — the root cause of the scattered "blue instead of
// gold" review issues. Do not remove this provider; prefer relying on it over
// re-adding per-field focus/checked sx.
//
// Brand rules (defect ledger + polish-pass answers):
// - Focus/selected field states and radios: CMP gold (#E1A731) — SH-002/CS-004.
// - Checkboxes and radios: one design look (Monday 13396402468, CMP Dashboard
//   (24) L43-51): 18px box, 1.5px #9E9E9E border on white; checked checkbox =
//   green border and green tick; checked radio = gold border and 10px gold
//   dot; disabled = 50% opacity; keyboard focus = 2px gold outline.
// - Switches and success states stay green (cmpGreen).
// - Errors/required asterisks: red.
const CMP_GOLD = commonStyles.cpaGold;
const CMP_GREEN = commonStyles.cmpGreen;
const CMP_ERROR_RED = commonStyles.errorRed;
const CHOICE_BORDER = "#9E9E9E";
const CHOICE_CHECKED_GREEN = CMP_GREEN;

// The 18px design box replaces MUI's 24px glyph (whose drawn box was 18px with
// a 3px inset). Root padding grows by those 3px, so a control keeps its old
// size and its box stays where it was (small: 10px, see sizeSmall); a
// screen that sets its own padding owns that layout.
const choiceControlRootSx = {
  padding: 12,
  "&:hover": { backgroundColor: "transparent" },
  "&.Mui-disabled": { cursor: "not-allowed", pointerEvents: "auto" },
  "& .cmp-choice-icon": {
    backgroundColor: "#FFFFFF",
    border: `1.5px solid ${CHOICE_BORDER}`,
    boxSizing: "border-box",
    display: "inline-block",
    flexShrink: 0,
    height: 18,
    position: "relative",
    transition: "border-color .15s ease",
    width: 18,
  },
  "&.Mui-disabled .cmp-choice-icon": { opacity: 0.5 },
  "&.Mui-focusVisible .cmp-choice-icon": {
    outline: `2px solid ${CMP_GOLD}`,
    outlineOffset: 2,
  },
} as const;

export const cmpTheme = createTheme({
  // MUI otherwise falls back to Roboto; the design uses DM Sans everywhere.
  typography: {
    fontFamily: "'DM Sans', sans-serif",
  },
  palette: {
    primary: {
      main: CMP_GOLD,
      contrastText: "#FFFFFF",
    },
    success: {
      main: CMP_GREEN,
    },
    error: {
      main: CMP_ERROR_RED,
    },
  },
  components: {
    // CMP Dashboard (21) "Pop-ups and action menus sit above IDA" (IF11 RP6):
    // menus, selects and popovers never lock page scrolling. Dialogs (Modal)
    // keep their scroll lock.
    MuiPopover: {
      defaultProps: {
        disableScrollLock: true,
      },
    },
    // The design checkbox: rounded 5px box, green border and tick when checked.
    MuiCheckbox: {
      defaultProps: {
        color: "success",
        disableRipple: true,
        icon: React.createElement(ChoiceControlIcon, { kind: "checkbox", state: "unchecked" }),
        checkedIcon: React.createElement(ChoiceControlIcon, { kind: "checkbox", state: "checked" }),
        indeterminateIcon: React.createElement(ChoiceControlIcon, { kind: "checkbox", state: "indeterminate" }),
      },
      styleOverrides: {
        root: {
          ...choiceControlRootSx,
          "& .cmp-choice-icon": { ...choiceControlRootSx["& .cmp-choice-icon"], borderRadius: 5 },
          "& .cmp-choice-icon--checked, & .cmp-choice-icon--indeterminate": {
            borderColor: CHOICE_CHECKED_GREEN,
          },
          "& .cmp-choice-icon--checked::after": {
            borderBottom: `2px solid ${CHOICE_CHECKED_GREEN}`,
            borderRadius: 1,
            borderRight: `2px solid ${CHOICE_CHECKED_GREEN}`,
            content: '""',
            height: 9,
            left: "50%",
            position: "absolute",
            top: "47%",
            transform: "translate(-50%, -55%) rotate(42deg)",
            width: 5,
          },
          // A part-selected group shows a green dash in the same box.
          "& .cmp-choice-icon--indeterminate::after": {
            backgroundColor: CHOICE_CHECKED_GREEN,
            borderRadius: 1,
            content: '""',
            height: 2,
            left: "50%",
            position: "absolute",
            top: "50%",
            transform: "translate(-50%, -50%)",
            width: 8,
          },
        },
        // A size slot, not a nested selector, so a screen's sx padding wins.
        sizeSmall: { padding: 10 },
      },
    },
    // The design radio: circle, gold border and 10px gold dot when selected.
    MuiRadio: {
      defaultProps: {
        color: "primary",
        disableRipple: true,
        icon: React.createElement(ChoiceControlIcon, { kind: "radio", state: "unchecked" }),
        checkedIcon: React.createElement(ChoiceControlIcon, { kind: "radio", state: "checked" }),
      },
      styleOverrides: {
        root: {
          ...choiceControlRootSx,
          "& .cmp-choice-icon": { ...choiceControlRootSx["& .cmp-choice-icon"], borderRadius: "50%" },
          "& .cmp-choice-icon--checked": { borderColor: CMP_GOLD },
          "& .cmp-choice-icon--checked::after": {
            backgroundColor: CMP_GOLD,
            borderRadius: "50%",
            content: '""',
            height: 10,
            left: "50%",
            position: "absolute",
            top: "50%",
            transform: "translate(-50%, -50%)",
            width: 10,
          },
        },
        sizeSmall: { padding: 10 },
      },
    },
    // Switches are green when on, matching the settings toggles.
    MuiSwitch: {
      defaultProps: {
        color: "success",
      },
      styleOverrides: {
        root: switchSx,
        switchBase: switchSx["& .MuiSwitch-switchBase"],
        thumb: switchSx["& .MuiSwitch-thumb"],
        track: ({ ownerState }) => ({
          ...switchSx["& .MuiSwitch-track"],
          ...(ownerState.checked
            ? switchSx["& .MuiSwitch-switchBase"]["&.Mui-checked"]["& + .MuiSwitch-track"]
            : {}),
          ...(ownerState.disabled
            ? switchSx["& .MuiSwitch-switchBase"]["&.Mui-disabled + .MuiSwitch-track"]
            : {}),
        }),
      },
    },
    // Gold focus ring/border for outlined inputs that have no local sx.
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: CMP_GOLD,
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          "&.Mui-focused": {
            color: CMP_GOLD,
          },
        },
      },
    },
  },
});
